import { createMemo, For, Match, Switch } from "solid-js";

// Slack message timestamps are epoch seconds with a uniqueness suffix
// ("1718012345.000200"). Returns "" during SSR: the server's TZ would hydrate
// wrong local text.
export function formatSlackTs(ts: string): string {
  if (typeof window === "undefined") return "";
  const seconds = Number.parseFloat(ts);
  if (!Number.isFinite(seconds)) return ts;
  const date = new Date(seconds * 1000);
  if (Number.isNaN(date.getTime())) return ts;
  return date.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

// Parsed Slack mrkdwn. Emphasis and quote nodes nest; code content is never
// re-parsed; `:emoji:` shortcodes stay as text.
type SlackNode =
  | { kind: "text"; text: string }
  | { kind: "mention"; text: string }
  | { kind: "channel"; text: string }
  | { kind: "link"; text: string; href: string }
  | { kind: "code"; text: string }
  | { kind: "codeblock"; text: string }
  | { kind: "quote"; children: SlackNode[] }
  | { kind: "bold"; children: SlackNode[] }
  | { kind: "italic"; children: SlackNode[] }
  | { kind: "strike"; children: SlackNode[] };

// Decode &amp; last so an already-encoded entity (&amp;lt;) round-trips to &lt;
// rather than collapsing to a literal "<". Structure is parsed before decoding,
// so a user-typed bracket can never be mistaken for a real Slack token.
function decodeSlackEntities(text: string): string {
  return text.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&");
}

const CODEBLOCK_RE = /```([\s\S]+?)```/;
const CODE_RE = /`([^`]+?)`/;
const MENTION_RE = /<@([UW][A-Z0-9]+)>/;
const CHANNEL_RE = /<#([A-Z0-9]+)(?:\|([^>]+))?>/;
const LINK_RE = /<(https?:\/\/[^|>]+)(?:\|([^>]*))?>/;
// Emphasis delimiters must sit on a word boundary and wrap a non-space run, so
// snake_case and "a * b" arithmetic never trigger formatting.
const MLQUOTE_RE = /(^|\n)>>>\s?([\s\S]*)$/;
const LNQUOTE_RE = /(^|\n)>\s?([^\n]*)/;
const BOLD_RE = /(^|[^\w*])\*([^\s*][^*\n]*?[^\s*]|[^\s*])\*(?=$|[^\w*])/;
const ITALIC_RE = /(^|[^\w_])_([^\s_][^_\n]*?[^\s_]|[^\s_])_(?=$|[^\w_])/;
const STRIKE_RE = /(^|[^\w~])~([^\s~][^~\n]*?[^\s~]|[^\s~])~(?=$|[^\w~])/;

interface Candidate {
  start: number;
  lead: number;
  end: number;
  node: SlackNode;
}

type Matcher = (s: string, mentions: Record<string, string> | undefined) => Candidate | null;

const emphasis =
  (re: RegExp, kind: "bold" | "italic" | "strike"): Matcher =>
  (s, mentions) => {
    const m = re.exec(s);
    if (!m) return null;
    const [whole, prefix, inner] = m;
    if (prefix === undefined || inner === undefined) return null;
    const start = m.index + prefix.length;
    return {
      start,
      lead: start,
      end: m.index + whole.length,
      node: { kind, children: parseSlackMrkdwn(inner, mentions) },
    };
  };

const quote =
  (re: RegExp): Matcher =>
  (s, mentions) => {
    const m = re.exec(s);
    if (!m) return null;
    const [whole, prefix, inner] = m;
    if (prefix === undefined || inner === undefined) return null;
    const start = m.index + prefix.length;
    return {
      start,
      lead: start,
      end: m.index + whole.length,
      node: { kind: "quote", children: parseSlackMrkdwn(inner, mentions) },
    };
  };

// Order resolves ties at the same start position: fenced code before inline
// code, broadcast quotes before single-line quotes.
const MATCHERS: Matcher[] = [
  (s) => {
    const m = CODEBLOCK_RE.exec(s);
    if (!m) return null;
    const [whole, body] = m;
    if (body === undefined) return null;
    return {
      start: m.index,
      lead: m.index,
      end: m.index + whole.length,
      node: { kind: "codeblock", text: decodeSlackEntities(body.replace(/^\n/, "").replace(/\n$/, "")) },
    };
  },
  (s) => {
    const m = CODE_RE.exec(s);
    if (!m) return null;
    const [whole, body] = m;
    if (body === undefined) return null;
    return {
      start: m.index,
      lead: m.index,
      end: m.index + whole.length,
      node: { kind: "code", text: decodeSlackEntities(body) },
    };
  },
  (s, mentions) => {
    const m = MENTION_RE.exec(s);
    if (!m) return null;
    const [whole, userId] = m;
    if (userId === undefined) return null;
    const name = mentions?.[userId];
    const node: SlackNode = name === undefined ? { kind: "text", text: whole } : { kind: "mention", text: name };
    return { start: m.index, lead: m.index, end: m.index + whole.length, node };
  },
  (s) => {
    const m = CHANNEL_RE.exec(s);
    if (!m) return null;
    const [whole, channelId, label] = m;
    if (channelId === undefined) return null;
    return {
      start: m.index,
      lead: m.index,
      end: m.index + whole.length,
      node: { kind: "channel", text: decodeSlackEntities(label ?? channelId) },
    };
  },
  (s) => {
    const m = LINK_RE.exec(s);
    if (!m) return null;
    const [whole, url, label] = m;
    if (url === undefined) return null;
    return {
      start: m.index,
      lead: m.index,
      end: m.index + whole.length,
      node: { kind: "link", text: decodeSlackEntities(label || url), href: decodeSlackEntities(url) },
    };
  },
  quote(MLQUOTE_RE),
  quote(LNQUOTE_RE),
  emphasis(BOLD_RE, "bold"),
  emphasis(ITALIC_RE, "italic"),
  emphasis(STRIKE_RE, "strike"),
];

function nextToken(s: string, mentions: Record<string, string> | undefined): Candidate | null {
  let best: Candidate | null = null;
  for (const match of MATCHERS) {
    const c = match(s, mentions);
    if (c && (best === null || c.start < best.start)) best = c;
  }
  return best;
}

// O(n²) worst case from re-scanning the remainder each token — fine for
// chat-message-sized text.
export function parseSlackMrkdwn(text: string, mentions: Record<string, string> | undefined): SlackNode[] {
  const nodes: SlackNode[] = [];
  const pushText = (raw: string) => {
    const decoded = decodeSlackEntities(raw);
    if (decoded) nodes.push({ kind: "text", text: decoded });
  };
  let s = text;
  while (s.length) {
    const c = nextToken(s, mentions);
    if (!c) {
      pushText(s);
      break;
    }
    if (c.lead > 0) pushText(s.slice(0, c.lead));
    nodes.push(c.node);
    s = s.slice(c.end);
  }
  return nodes;
}

function nodeOf<K extends SlackNode["kind"]>(node: SlackNode, kind: K): Extract<SlackNode, { kind: K }> | undefined {
  return node.kind === kind ? (node as Extract<SlackNode, { kind: K }>) : undefined;
}

const SlackNodes = (props: { nodes: SlackNode[] }) => (
  <For each={props.nodes}>{(node) => <SlackNodeView node={node} />}</For>
);

const SlackNodeView = (props: { node: SlackNode }) => (
  <Switch fallback={nodeOf(props.node, "text")?.text}>
    <Match when={nodeOf(props.node, "mention")}>
      {(n) => <span class="rounded bg-brand/10 px-1 text-brand">@{n().text}</span>}
    </Match>
    <Match when={nodeOf(props.node, "channel")}>
      {(n) => <span class="rounded bg-brand/10 px-1 text-brand">#{n().text}</span>}
    </Match>
    <Match when={nodeOf(props.node, "link")}>
      {(n) => (
        <a
          href={n().href}
          target="_blank"
          rel="noopener noreferrer"
          class="wrap-break-word text-brand underline underline-offset-2 hover:text-brand/80"
        >
          {n().text}
        </a>
      )}
    </Match>
    <Match when={nodeOf(props.node, "code")}>
      {(n) => <code class="rounded bg-inset px-1 font-mono text-[0.85em]">{n().text}</code>}
    </Match>
    <Match when={nodeOf(props.node, "codeblock")}>
      {(n) => <code class="my-1 block overflow-x-auto rounded bg-inset px-2 py-1 font-mono text-xs">{n().text}</code>}
    </Match>
    <Match when={nodeOf(props.node, "quote")}>
      {(n) => (
        <span class="border-l-2 border-primary-border pl-2 text-muted-text">
          <SlackNodes nodes={n().children} />
        </span>
      )}
    </Match>
    <Match when={nodeOf(props.node, "bold")}>
      {(n) => (
        <strong>
          <SlackNodes nodes={n().children} />
        </strong>
      )}
    </Match>
    <Match when={nodeOf(props.node, "italic")}>
      {(n) => (
        <em>
          <SlackNodes nodes={n().children} />
        </em>
      )}
    </Match>
    <Match when={nodeOf(props.node, "strike")}>
      {(n) => (
        <s>
          <SlackNodes nodes={n().children} />
        </s>
      )}
    </Match>
  </Switch>
);

export const SlackMessageText = (props: { text: string; mentions: Record<string, string> | undefined }) => {
  const nodes = createMemo(() => parseSlackMrkdwn(props.text ?? "", props.mentions));
  return <SlackNodes nodes={nodes()} />;
};
